import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, BookOpen, Pin } from 'lucide-react';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function FeaturedPostSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFeatured = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?status=published&limit=1`);
      if (response.data && response.data.length > 0) {
        setPost(response.data[0]);
      }
    } catch (error) {
      logError('Error fetching featured post:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  if (!loading && !post) return null;

  const readMinutes = post
    ? Math.max(1, Math.ceil((post.excerpt?.split(' ').length || 0) / 50) + 2)
    : 3;

  return (
    <section
      id="blog"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="featured-post-section"
    >
      {/* Background accents — restrained, monochrome */}
      <div className="absolute inset-0 strategic-grid opacity-50" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/[0.02] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-signal-glow rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
        >
          <div>
            <p className="eyebrow section-eyebrow-line mb-6">
              Featured Insight
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark tracking-tight">
              The{' '}
              <span className="text-gradient">Conversation</span>{' '}
              We're Leading
            </h2>
          </div>

          <Link
            to="/blog"
            className="link-underline inline-flex items-center gap-2 text-stark hover:text-stark transition-colors group text-sm font-medium"
            data-testid="view-all-posts-link"
          >
            View All Posts
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Featured Card */}
        {loading ? (
          <div className="rounded-3xl bg-white/5 h-[420px] md:h-[460px] animate-pulse" />
        ) : (
          <motion.article
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="featured-post-card"
          >
            <Link
              to={`/blog/${post.slug}`}
              className="group block relative overflow-hidden rounded-md border border-line-1 hover:border-line-3 transition-colors duration-500 bg-surface"
            >
              <div className="grid md:grid-cols-2 min-h-[420px]">
                {/* Image */}
                {post.featured_image && (
                  <div className="relative h-64 md:h-auto overflow-hidden border-r border-line-1">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-ink/60 md:to-ink/80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent md:hidden" />
                  </div>
                )}

                {/* Content */}
                <div className="relative p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-5">
                    {post.category && (
                      <span className="focus-tag inline-flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" />
                        {post.category}
                      </span>
                    )}
                    <span className="eyebrow text-signal flex items-center gap-1.5">
                      <Pin className="w-3 h-3" />
                      Pinned
                    </span>
                  </div>

                  <h3
                    className="font-heading text-2xl md:text-4xl font-bold text-stark mb-4 group-hover:text-stark transition-colors duration-300 leading-tight tracking-tight"
                    data-testid="featured-post-title"
                  >
                    {post.title}
                  </h3>

                  <p className="text-stark-2 text-base md:text-lg leading-relaxed mb-6 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex flex-wrap items-center gap-5 text-sm text-stark-3 mb-8">
                    <span className="flex items-center gap-1.5">
                      <User className="w-4 h-4" />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {new Date(post.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="font-mono">{readMinutes} min read</span>
                  </div>

                  <div className="inline-flex items-center gap-2 text-stark font-semibold group-hover:gap-3 transition-all">
                    Read Full Article
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.article>
        )}
      </div>
    </section>
  );
}

export default FeaturedPostSection;
